import React, { useState, useEffect } from 'react';
import { api, apiCall } from './config/apiConfig';
import { useAuth } from './AuthContext';

const creditPackages = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 100,
    price: 29,
  },
  {
    id: 'pro',
    name: 'Professional',
    credits: 500,
    price: 99,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    credits: 2000,
    price: 299,
  },
];

interface RegisterProps {
  onBack: () => void;
  onGoToLogin: () => void;
  onRegisterSuccess: () => void;
}

const Register: React.FC<RegisterProps> = ({ onBack, onGoToLogin, onRegisterSuccess }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState<string | null>(null);
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get('plan');
    if (planParam && creditPackages.some(pkg => pkg.id === planParam)) {
      setPlan(planParam);
    } else {
      setPlan(null);
    }
  }, []);

  const handleStripeRedirect = async (planId: string, token: string) => {
    const pkg = creditPackages.find(p => p.id === planId);
    if (!pkg) return;
    setRedirectingToStripe(true);
    try {
      const res = await apiCall('/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: pkg.price, credits: pkg.credits })
      }, token);
      if (!res.ok) {
        const errText = await res.text();
        setError('Failed to start payment: ' + errText);
        setRedirectingToStripe(false);
        return;
      }
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Stripe checkout URL missing.');
        setRedirectingToStripe(false);
      }
    } catch (err) {
      setError('Network error during payment.');
      setRedirectingToStripe(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.register({ username: email, password });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Registration failed');
        setIsLoading(false);
        return;
      }
      // Automatically log in after successful registration
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      const loginRes = await api.login(formData);
      if (!loginRes.ok) {
        setError('Registration succeeded, but login failed. Please sign in.');
        setIsLoading(false);
        onRegisterSuccess();
        return;
      }
      const loginData = await loginRes.json();
      login(loginData.access_token, { email });
      setIsLoading(false);
      // If a plan was selected, redirect to Stripe with token
      if (plan) {
        await handleStripeRedirect(plan, loginData.access_token);
        return;
      }
      onRegisterSuccess();
    } catch (err) {
      setError('Network error');
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {redirectingToStripe && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(10,10,10,0.75)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ width: 48, height: 48, border: '6px solid #e5e7eb', borderTop: '6px solid #4ecdc4', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 18 }} />
          <div style={{ color: 'white', fontSize: 20, fontWeight: 600 }}>Redirecting to Stripe...</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div className="auth-form" style={{ position: 'relative', overflow: 'visible' }}>
        {/* Registration Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div style={{
            background: 'linear-gradient(135deg, #4ecdc4 0%, #ff6b6b 100%)',
            borderRadius: '50%',
            width: 72,
            height: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 24px rgba(76,205,196,0.10)',
          }}>
            {/* User Plus Icon */}
            <svg width="36" height="36" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" />
              <path d="M16 16v2a4 4 0 0 1-8 0v-2" />
              <line x1="20" y1="12" x2="20" y2="18" />
              <line x1="17" y1="15" x2="23" y2="15" />
            </svg>
          </div>
        </div>
        <h2 style={{ marginBottom: 8, fontSize: 28 }}>Create your account</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div className="input-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              id="register-email"
              autoComplete="username"
            />
            <span className="input-icon">
              <svg width="18" height="18" fill="none" stroke="#4ecdc4" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16v16H4z" fill="none"/><path d="M22 6l-10 7L2 6" /></svg>
            </span>
          </div>
          <div className="input-group" style={{ position: 'relative' }}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              id="register-password"
              autoComplete="new-password"
            />
            <span className="input-icon">
              <svg width="18" height="18" fill="none" stroke="#4ecdc4" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
            </span>
          </div>
          <div className="input-group" style={{ position: 'relative' }}>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              id="register-confirm"
              autoComplete="new-password"
            />
            <span className="input-icon">
              <svg width="18" height="18" fill="none" stroke="#4ecdc4" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
            </span>
          </div>
          {/* Error */}
          {error && <div style={{ color: '#ff6b6b', fontSize: 14, marginBottom: -12 }}>{error}</div>}
          <button type="submit" className="btn-primary" disabled={isLoading} style={{ marginTop: 8 }}>
            {isLoading ? 'Registering...' : 'Register'}
          </button>
          {/* Login Link */}
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <span style={{ color: '#aaa', fontSize: 14 }}>Already have an account? </span>
            <button
              type="button"
              onClick={onGoToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: '#4ecdc4',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'underline'
              }}
            >
              Sign in
            </button>
          </div>
          <button type="button" onClick={onBack} className="btn-secondary" style={{ marginTop: 18 }}>
            Back
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
